import {FC, useRef, useState} from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Link as MuiLink,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import {CloudUpload, Delete, Edit, Image as ImageIcon} from '@mui/icons-material';
import {AiImageGroup} from '../../types/aiImage';
import {useDeleteAiImageGroup, usePatchAiImageGroup} from '../../hooks/useAiImages';
import {uploadApi} from '../../api/uploadApi';

interface Props {
    group: AiImageGroup;
    onDeleted: () => void;
}

export const AiImageGroupHeader: FC<Props> = ({group, onDeleted}) => {
    const [editOpen, setEditOpen] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [name, setName] = useState(group.name);
    const [systemContext, setSystemContext] = useState(group.systemContext);
    const [description, setDescription] = useState(group.description ?? '');
    const [editError, setEditError] = useState<string | null>(null);

    const updateMut = usePatchAiImageGroup(group.id);
    const deleteMut = useDeleteAiImageGroup();

    // base image 업로드/제거 상태 (헤더 안에서 즉시 처리)
    const baseInputRef = useRef<HTMLInputElement>(null);
    const [baseBusy, setBaseBusy] = useState(false);
    const [baseError, setBaseError] = useState<string | null>(null);

    const handleBaseUpload = async (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        const file = event.target.files?.[0];
        if (baseInputRef.current) baseInputRef.current.value = '';
        if (!file) return;

        setBaseError(null);
        if (!file.type.startsWith('image/')) {
            setBaseError('이미지 파일만 업로드 가능합니다.');
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            setBaseError('파일 크기는 5MB 이하여야 합니다.');
            return;
        }

        try {
            setBaseBusy(true);
            const res = await uploadApi.uploadImage(file, 'ai-image-groups');
            await updateMut.mutateAsync({defaultBaseImageUrl: res.fileUrl});
        } catch (e) {
            setBaseError(e instanceof Error ? e.message : '업로드 실패');
        } finally {
            setBaseBusy(false);
        }
    };

    const handleBaseClear = async () => {
        setBaseError(null);
        try {
            setBaseBusy(true);
            // 빈 문자열 = 백엔드가 DB NULL 로 클리어
            await updateMut.mutateAsync({defaultBaseImageUrl: ''});
        } catch (e) {
            setBaseError(e instanceof Error ? e.message : '제거 실패');
        } finally {
            setBaseBusy(false);
        }
    };

    const openEdit = () => {
        setName(group.name);
        setSystemContext(group.systemContext);
        setDescription(group.description ?? '');
        setEditError(null);
        setEditOpen(true);
    };

    const closeEdit = () => {
        if (updateMut.isPending) return;
        setEditOpen(false);
    };

    const handleSave = async () => {
        if (!name.trim() || !systemContext.trim()) {
            setEditError('그룹명과 시스템 컨텍스트는 필수입니다.');
            return;
        }
        try {
            await updateMut.mutateAsync({
                name: name.trim(),
                systemContext: systemContext.trim(),
                description: description.trim() || undefined,
            });
            setEditOpen(false);
        } catch (e) {
            setEditError(e instanceof Error ? e.message : '수정 실패');
        }
    };

    const handleDelete = async () => {
        try {
            await deleteMut.mutateAsync(group.id);
            setConfirmDelete(false);
            onDeleted();
        } catch (e) {
            alert(e instanceof Error ? e.message : '삭제 실패');
        }
    };

    return (
        <Card>
            <CardContent>
                <Stack direction="row" alignItems="flex-start" spacing={1}>
                    <Box sx={{flex: 1, minWidth: 0}}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="h6" fontWeight={700} noWrap>
                                {group.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                #{group.id}
                            </Typography>
                        </Stack>
                        {group.description && (
                            <Typography variant="body2" color="text.secondary" sx={{mt: 0.5}}>
                                {group.description}
                            </Typography>
                        )}
                        <Box
                            sx={{
                                mt: 1.5,
                                p: 1.5,
                                bgcolor: 'grey.50',
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'divider',
                            }}
                        >
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                fontWeight={600}
                                sx={{display: 'block', mb: 0.5}}
                            >
                                시스템 컨텍스트
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                    fontFamily: 'ui-monospace, monospace',
                                    fontSize: '0.78rem',
                                }}
                            >
                                {group.systemContext}
                            </Typography>
                        </Box>

                        {/* 기본 base 이미지 영역 — 그룹에 base 가 있으면 그 그룹의 모든 신규 generate 요청이 자동 prefill */}
                        <Box
                            sx={{
                                mt: 1.5,
                                p: 1.5,
                                border: '1px dashed',
                                borderColor: 'divider',
                                borderRadius: 1,
                                bgcolor: 'action.hover',
                            }}
                        >
                            <Stack
                                direction="row"
                                alignItems="flex-start"
                                spacing={1.5}
                                flexWrap="wrap"
                            >
                                <Box sx={{minWidth: 60, height: 60}}>
                                    {group.defaultBaseImageUrl ? (
                                        <Box
                                            component="a"
                                            href={group.defaultBaseImageUrl}
                                            target="_blank"
                                            rel="noreferrer"
                                            sx={{display: 'block'}}
                                        >
                                            <Box
                                                component="img"
                                                src={group.defaultBaseImageUrl}
                                                alt="base"
                                                sx={{
                                                    width: 60,
                                                    height: 60,
                                                    objectFit: 'contain',
                                                    bgcolor: 'grey.50',
                                                    border: '1px solid',
                                                    borderColor: 'divider',
                                                    borderRadius: 1,
                                                }}
                                            />
                                        </Box>
                                    ) : (
                                        <Box
                                            sx={{
                                                width: 60,
                                                height: 60,
                                                bgcolor: 'grey.100',
                                                border: '1px solid',
                                                borderColor: 'divider',
                                                borderRadius: 1,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <ImageIcon
                                                fontSize="small"
                                                color="disabled"
                                            />
                                        </Box>
                                    )}
                                </Box>
                                <Stack spacing={0.5} sx={{flexGrow: 1, minWidth: 0}}>
                                    <Typography variant="body2" fontWeight={600}>
                                        기본 base 이미지
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        이 그룹의 모든 신규 generate 요청 폼에서 자동
                                        prefill 됩니다. (PNG/JPG · 최대 5MB)
                                    </Typography>
                                    {group.defaultBaseImageUrl && (
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                fontFamily: 'ui-monospace, monospace',
                                                fontSize: '0.72rem',
                                                wordBreak: 'break-all',
                                            }}
                                        >
                                            <MuiLink
                                                href={group.defaultBaseImageUrl}
                                                target="_blank"
                                                rel="noreferrer"
                                            >
                                                {group.defaultBaseImageUrl}
                                            </MuiLink>
                                        </Typography>
                                    )}
                                </Stack>
                                <Stack direction="row" spacing={1}>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={
                                            baseBusy ? (
                                                <CircularProgress size={14} />
                                            ) : (
                                                <CloudUpload />
                                            )
                                        }
                                        disabled={baseBusy}
                                        onClick={() => baseInputRef.current?.click()}
                                    >
                                        {baseBusy
                                            ? '업로드 중...'
                                            : group.defaultBaseImageUrl
                                              ? '교체'
                                              : '업로드'}
                                    </Button>
                                    {group.defaultBaseImageUrl && (
                                        <Button
                                            variant="text"
                                            color="error"
                                            size="small"
                                            disabled={baseBusy}
                                            onClick={handleBaseClear}
                                        >
                                            제거
                                        </Button>
                                    )}
                                </Stack>
                            </Stack>
                            {baseError && (
                                <Alert
                                    severity="error"
                                    sx={{mt: 1}}
                                    onClose={() => setBaseError(null)}
                                >
                                    {baseError}
                                </Alert>
                            )}
                            <input
                                ref={baseInputRef}
                                type="file"
                                accept="image/*"
                                onChange={handleBaseUpload}
                                style={{display: 'none'}}
                            />
                        </Box>
                    </Box>
                    <Stack direction="row" spacing={0.5}>
                        <Tooltip title="편집">
                            <IconButton size="small" onClick={openEdit}>
                                <Edit fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="그룹 삭제">
                            <IconButton
                                size="small"
                                color="error"
                                onClick={() => setConfirmDelete(true)}
                            >
                                <Delete fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                </Stack>
            </CardContent>

            {/* 편집 다이얼로그 */}
            <Dialog open={editOpen} onClose={closeEdit} fullWidth maxWidth="sm">
                <DialogTitle>그룹 편집</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{mt: 1}}>
                        <TextField
                            label="그룹명"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            fullWidth
                            required
                        />
                        <TextField
                            label="시스템 컨텍스트"
                            value={systemContext}
                            onChange={(e) => setSystemContext(e.target.value)}
                            fullWidth
                            required
                            multiline
                            rows={6}
                        />
                        <TextField
                            label="설명 (선택)"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            fullWidth
                            multiline
                            rows={2}
                        />
                        {editError && <Alert severity="error">{editError}</Alert>}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeEdit} disabled={updateMut.isPending}>
                        취소
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={updateMut.isPending}
                    >
                        {updateMut.isPending ? '저장 중...' : '저장'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* 삭제 확인 */}
            <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)}>
                <DialogTitle>그룹 삭제</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        "{group.name}" 그룹을 삭제하시겠습니까? 그룹의 모든 요청과
                        이미지 결과가 함께 삭제됩니다. (R2 객체는 별도 정리)
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDelete(false)} disabled={deleteMut.isPending}>
                        취소
                    </Button>
                    <Button
                        color="error"
                        variant="contained"
                        onClick={handleDelete}
                        disabled={deleteMut.isPending}
                    >
                        {deleteMut.isPending ? '삭제 중...' : '삭제'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Card>
    );
};
